import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { CommentType } from "../../services/types";

type CommentNavigatorMode = "overview" | "drill" | "lanes" | "recent";

type CommentNode = Omit<CommentType, "parentId" | "replies"> & {
  parentId: number | null;
  replies: CommentNode[];
};

interface DiscussionCommentsNavigatorProps {
  comments?: CommentType[];
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

const stripHtml = (html: string): string => {
  const cleaned = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  return cleaned.replace(/\s+/g, " ").trim();
};

const formatRelativeTime = (dateStr?: string | null): string => {
  if (!dateStr) return "unknown time";
  const parsed = Date.parse(dateStr);
  if (Number.isNaN(parsed)) return "unknown time";

  const diff = Date.now() - parsed;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return "just now";
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))} min ago`;
  if (diff < day) return `${Math.max(1, Math.floor(diff / hour))} hours ago`;
  if (diff < 2 * day) return "yesterday";
  if (diff < 30 * day) return `${Math.floor(diff / day)} days ago`;

  return dateFormatter.format(parsed);
};

const getTimestamp = (dateStr?: string | null): number => {
  if (!dateStr) return 0;
  const parsed = Date.parse(dateStr);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const buildCommentTree = (comments: CommentType[]): CommentNode[] => {
  const map = new Map<number, CommentNode>();

  comments.forEach((comment) => {
    map.set(comment.id, {
      ...comment,
      parentId: comment.parentId ?? null,
      replies: [],
    });
  });

  const roots: CommentNode[] = [];

  comments.forEach((comment) => {
    const node = map.get(comment.id);
    if (!node) return;

    const parentId = comment.parentId ?? null;
    const parent = parentId === null ? null : map.get(parentId);

    if (!parent) {
      roots.push(node);
      return;
    }

    parent.replies.push(node);
  });

  const sortRecursive = (nodes: CommentNode[]): CommentNode[] => {
    nodes.sort((a, b) => getTimestamp(b.createdAt) - getTimestamp(a.createdAt));
    nodes.forEach((node) => sortRecursive(node.replies));
    return nodes;
  };

  return sortRecursive(roots);
};

const flattenComments = (nodes: CommentNode[]): CommentNode[] => {
  const result: CommentNode[] = [];
  const walk = (items: CommentNode[]) => {
    items.forEach((item) => {
      result.push(item);
      walk(item.replies);
    });
  };
  walk(nodes);
  return result;
};

const countAllReplies = (node: CommentNode): number => {
  return node.replies.reduce((count, reply) => count + 1 + countAllReplies(reply), 0);
};

const findCommentById = (nodes: CommentNode[], id: number | null): CommentNode | null => {
  if (id === null) return null;
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findCommentById(node.replies, id);
    if (found) return found;
  }
  return null;
};

const getPathNodes = (roots: CommentNode[], path: number[]): CommentNode[] => {
  const result: CommentNode[] = [];
  let currentLevel = roots;

  for (const id of path) {
    const node = currentLevel.find((item) => item.id === id);
    if (!node) break;
    result.push(node);
    currentLevel = node.replies;
  }

  return result;
};

const getInitialPath = (roots: CommentNode[]): number[] => {
  return roots[0] ? [roots[0].id] : [];
};

const CommentAuthor: React.FC<{ comment: CommentNode }> = ({ comment }) => {
  const name = comment.user?.name || "Anonymous";
  return (
    <span className="discussion-comments-author">
      <span className="discussion-comments-avatar">{name.slice(0, 1).toUpperCase()}</span>
      <span>{name}</span>
    </span>
  );
};

const CommentBody: React.FC<{ comment: CommentNode }> = ({ comment }) => (
  <p className="discussion-comments-text">{stripHtml(comment.comment)}</p>
);

const CommentCard: React.FC<{
  comment: CommentNode;
  selected?: boolean;
  level?: number;
  siblingIndex?: number;
  siblingCount?: number;
  onSelect?: () => void;
  onOpenReplies?: () => void;
}> = ({
  comment,
  selected = false,
  level = 1,
  siblingIndex = 0,
  siblingCount = 1,
  onSelect,
  onOpenReplies,
}) => {
  const deepReplyCount = countAllReplies(comment);
  const directReplyCount = comment.replies.length;
  const hasReplies = directReplyCount > 0;

  return (
    <article
      className={`discussion-comments-card discussion-comments-level-${Math.min(level, 4)}${
        selected ? " is-selected" : ""
      }`}
    >
      <div className="discussion-comments-card-head">
        <CommentAuthor comment={comment} />
        <span className="discussion-comments-time">{formatRelativeTime(comment.createdAt)}</span>
      </div>

      <CommentBody comment={comment} />

      <div className="discussion-comments-card-foot">
        <div className="discussion-comments-position">
          <span>Level {level}</span>
          <span>·</span>
          <span>
            sibling {siblingIndex + 1} of {siblingCount}
          </span>
          <span className="discussion-comments-sibling-dots" aria-hidden="true">
            {Array.from({ length: Math.min(siblingCount, 8) }).map((_, index) => (
              <span
                key={index}
                className={`discussion-comments-sibling-dot${index === siblingIndex ? " is-active" : ""}`}
              />
            ))}
          </span>
        </div>

        <div className="discussion-comments-card-actions">
          <button type="button" className="discussion-comments-link" onClick={onSelect}>
            Select
          </button>
          {hasReplies && (
            <button type="button" className="discussion-comments-link is-primary" onClick={onOpenReplies}>
              View replies ({directReplyCount})
            </button>
          )}
          {deepReplyCount > directReplyCount && (
            <span className="discussion-comments-count-note">+{deepReplyCount - directReplyCount} deeper</span>
          )}
        </div>
      </div>
    </article>
  );
};

const DiscussionCommentsNavigator: React.FC<DiscussionCommentsNavigatorProps> = ({ comments = [] }) => {
  const roots = useMemo(() => buildCommentTree(comments), [comments]);
  const allComments = useMemo(() => flattenComments(roots), [roots]);
  const [mode, setMode] = useState<CommentNavigatorMode>("overview");
  const [selectedPath, setSelectedPath] = useState<number[]>(() => getInitialPath(roots));

  useEffect(() => {
    setSelectedPath(getInitialPath(roots));
    setMode("overview");
  }, [roots]);

  const pathNodes = useMemo(() => getPathNodes(roots, selectedPath), [roots, selectedPath]);
  const selectedComment = pathNodes[pathNodes.length - 1] ?? null;
  const topLevelSelectedId = selectedPath[0] ?? null;
  const secondLevelSelectedId = selectedPath[1] ?? null;
  const selectedTopLevel = findCommentById(roots, topLevelSelectedId);
  const selectedSecondLevel = selectedTopLevel
    ? findCommentById(selectedTopLevel.replies, secondLevelSelectedId)
    : null;

  const recentComments = useMemo(
    () => [...allComments].sort((a, b) => getTimestamp(b.createdAt) - getTimestamp(a.createdAt)).slice(0, 6),
    [allComments]
  );

  if (comments.length === 0 || roots.length === 0) {
    return (
      <section className="discussion-comments-navigator">
        <div className="discussion-comments-head">
          <div>
            <h3>Comments Navigator</h3>
            <p>No comments yet for this selected GIST.</p>
          </div>
        </div>
        <div className="discussion-comments-empty">Comments will appear here as a compact navigator.</div>
      </section>
    );
  }

  const selectPath = (path: number[], nextMode: CommentNavigatorMode = mode) => {
    setSelectedPath(path);
    setMode(nextMode);
  };

  const renderPathBar = () => (
    <div className="discussion-comments-pathbar">
      <span className="discussion-comments-path-label">Path</span>
      <button
        type="button"
        className={`discussion-comments-crumb${pathNodes.length === 0 ? " is-current" : ""}`}
        onClick={() => {
          setSelectedPath(getInitialPath(roots));
          setMode("overview");
        }}
      >
        All comments
      </button>
      {pathNodes.map((node, index) => (
        <button
          type="button"
          key={node.id}
          className={`discussion-comments-crumb${index === pathNodes.length - 1 ? " is-current" : ""}`}
          onClick={() => selectPath(selectedPath.slice(0, index + 1), "drill")}
        >
          {index + 1}. {stripHtml(node.comment).slice(0, 34)}
        </button>
      ))}
      <div className="discussion-comments-path-actions">
        <button type="button" className="discussion-comments-link" onClick={() => setMode("overview")}>Overview</button>
        <button type="button" className="discussion-comments-link" onClick={() => setMode("lanes")}>Lanes</button>
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="discussion-comments-mode">
      {renderPathBar()}
      <div className="discussion-comments-overview-layout">
        <div className="discussion-comments-list">
          {roots.map((comment, index) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              selected={selectedPath[0] === comment.id}
              level={1}
              siblingIndex={index}
              siblingCount={roots.length}
              onSelect={() => selectPath([comment.id], "overview")}
              onOpenReplies={() => selectPath([comment.id], "drill")}
            />
          ))}
        </div>
        <aside className="discussion-comments-side-note">
          <h4>Overview mode</h4>
          <p>
            Start with top-level comments only. This keeps the first view readable and shows which
            comments have reply branches.
          </p>
          <ul>
            <li>scan top-level comments vertically;</li>
            <li>click View replies to enter one comment branch;</li>
            <li>comments stay out of the GIST tree.</li>
          </ul>
        </aside>
      </div>
    </div>
  );

  const renderDrill = () => {
    const topLevelPath = selectedPath[0] ? [selectedPath[0]] : getInitialPath(roots);
    const selected = selectedComment ?? roots[0];
    const replies = selected?.replies ?? [];

    return (
      <div className="discussion-comments-mode">
        {renderPathBar()}
        <div className="discussion-comments-drill-layout">
          <aside className="discussion-comments-sidebar">
            <div className="discussion-comments-sidebar-title">Top-level comments</div>
            {roots.map((comment) => (
              <button
                type="button"
                key={comment.id}
                className={`discussion-comments-side-item${selectedPath[0] === comment.id ? " is-active" : ""}`}
                onClick={() => selectPath([comment.id], "drill")}
              >
                <strong>{stripHtml(comment.comment).slice(0, 70)}</strong>
                <span>{comment.replies.length} replies · {formatRelativeTime(comment.createdAt)}</span>
              </button>
            ))}
          </aside>

          <div className="discussion-comments-drill-main">
            {selected && (
              <article className="discussion-comments-focus-card">
                <span className="discussion-comments-focus-label">Selected comment</span>
                <CommentAuthor comment={selected} />
                <CommentBody comment={selected} />
                <div className="discussion-comments-focus-actions">
                  {selectedPath.length > 1 && (
                    <button
                      type="button"
                      className="discussion-comments-link"
                      onClick={() => selectPath(selectedPath.slice(0, -1), "drill")}
                    >
                      Back to parent
                    </button>
                  )}
                  <button type="button" className="discussion-comments-link" onClick={() => selectPath(topLevelPath, "overview")}>
                    Back to top-level
                  </button>
                </div>
              </article>
            )}

            <div className="discussion-comments-replies-title">
              Replies to selected comment <span>{replies.length}</span>
            </div>
            <div className="discussion-comments-replies-stack">
              {replies.length > 0 ? (
                replies.map((reply, index) => (
                  <CommentCard
                    key={reply.id}
                    comment={reply}
                    selected={selectedPath.includes(reply.id)}
                    level={selectedPath.length + 1}
                    siblingIndex={index}
                    siblingCount={replies.length}
                    onSelect={() => selectPath([...selectedPath, reply.id], "drill")}
                    onOpenReplies={() => selectPath([...selectedPath, reply.id], "drill")}
                  />
                ))
              ) : (
                <div className="discussion-comments-empty">No replies under this comment yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLanes = () => {
    const lane1 = roots;
    const lane2 = selectedTopLevel?.replies ?? [];
    const lane3 = selectedSecondLevel?.replies ?? [];

    return (
      <div className="discussion-comments-mode">
        {renderPathBar()}
        <div className="discussion-comments-lanes">
          <section className="discussion-comments-lane">
            <div className="discussion-comments-lane-head">
              <strong>Lane 1</strong>
              <span>Top-level comments to this GIST</span>
            </div>
            <div className="discussion-comments-lane-body">
              {lane1.map((comment) => (
                <button
                  type="button"
                  key={comment.id}
                  className={`discussion-comments-lane-card${selectedPath[0] === comment.id ? " is-active" : ""}`}
                  onClick={() => selectPath([comment.id], "lanes")}
                >
                  <strong>{stripHtml(comment.comment).slice(0, 62)}</strong>
                  <span>{comment.replies.length} replies</span>
                </button>
              ))}
            </div>
          </section>

          <section className="discussion-comments-lane">
            <div className="discussion-comments-lane-head">
              <strong>Lane 2</strong>
              <span>Replies to selected Lane 1 comment</span>
            </div>
            <div className="discussion-comments-lane-body">
              {lane2.length > 0 ? lane2.map((comment) => (
                <button
                  type="button"
                  key={comment.id}
                  className={`discussion-comments-lane-card${selectedPath[1] === comment.id ? " is-active" : ""}`}
                  onClick={() => selectPath([selectedPath[0], comment.id].filter(Boolean) as number[], "lanes")}
                >
                  <strong>{stripHtml(comment.comment).slice(0, 62)}</strong>
                  <span>{comment.replies.length} children</span>
                </button>
              )) : <div className="discussion-comments-lane-empty">Select a top-level comment with replies.</div>}
            </div>
          </section>

          <section className="discussion-comments-lane">
            <div className="discussion-comments-lane-head">
              <strong>Lane 3</strong>
              <span>Replies to selected Lane 2 comment</span>
            </div>
            <div className="discussion-comments-lane-body">
              {lane3.length > 0 ? lane3.map((comment) => (
                <button
                  type="button"
                  key={comment.id}
                  className={`discussion-comments-lane-card${selectedPath[2] === comment.id ? " is-active" : ""}`}
                  onClick={() => selectPath([selectedPath[0], selectedPath[1], comment.id].filter(Boolean) as number[], "lanes")}
                >
                  <strong>{stripHtml(comment.comment).slice(0, 62)}</strong>
                  <span>{comment.replies.length} children</span>
                </button>
              )) : <div className="discussion-comments-lane-empty">Select a reply with children.</div>}
            </div>
          </section>
        </div>
        <div className="discussion-comments-note">
          <strong>Lanes rule:</strong> Lane 2 depends on the selected item in Lane 1. Lane 3 depends on
          the selected item in Lane 2. These are not global comment levels.
        </div>
      </div>
    );
  };

  const renderRecent = () => (
    <div className="discussion-comments-mode">
      {renderPathBar()}
      <div className="discussion-comments-recent-list">
        {recentComments.map((comment) => (
          <button
            type="button"
            key={comment.id}
            className="discussion-comments-recent-item"
            onClick={() => setSelectedPath([comment.id])}
          >
            <span className="discussion-comments-recent-time">{formatRelativeTime(comment.createdAt)}</span>
            <span className="discussion-comments-recent-copy">{stripHtml(comment.comment).slice(0, 110)}</span>
            <span className="discussion-comments-recent-count">{comment.replies.length} replies</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section className="discussion-comments-navigator">
      <div className="discussion-comments-head">
        <div>
          <h3>Comments Navigator</h3>
          <p>Browse comments as short side-discussion, without adding them to the GIST tree.</p>
        </div>
        <div className="discussion-comments-tabs" role="tablist" aria-label="Comment navigation mode">
          <button
            type="button"
            className={`discussion-comments-tab${mode === "overview" ? " is-active" : ""}`}
            onClick={() => setMode("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            className={`discussion-comments-tab${mode === "drill" ? " is-active" : ""}`}
            onClick={() => setMode("drill")}
          >
            Drill-down
          </button>
          <button
            type="button"
            className={`discussion-comments-tab${mode === "lanes" ? " is-active" : ""}`}
            onClick={() => setMode("lanes")}
          >
            Lanes
          </button>
          <button
            type="button"
            className={`discussion-comments-tab${mode === "recent" ? " is-active" : ""}`}
            onClick={() => setMode("recent")}
          >
            Recent
          </button>
        </div>
      </div>

      <div className="discussion-comments-stats">
        <span><strong>{allComments.length}</strong> total</span>
        <span><strong>{roots.length}</strong> top-level</span>
        <span><strong>{Math.max(...allComments.map((comment) => countAllReplies(comment)), 0)}</strong> largest branch</span>
      </div>

      <div className="discussion-comments-body">
        {mode === "overview" && renderOverview()}
        {mode === "drill" && renderDrill()}
        {mode === "lanes" && renderLanes()}
        {mode === "recent" && renderRecent()}
      </div>
    </section>
  );
};

export default DiscussionCommentsNavigator;
